/*
 * The reply agent's state as a mark and a word, never a tone pill (design review "Label colours":
 * the Agent Speaks in Words Rule, which Dawid agreed). Labels own hue; the agent borrowed blue,
 * slate, amber and red from them, so "Question" and "Ready to call" read alike, and "Asked to stop"
 * and "Check Bison" shared a red fill. Now:
 * - the rep's move (Draft ready, Needs your answer, Ready to call): ink at 600, a bronze pencil;
 * - waiting on the agent (Drafting, No draft, Sending…): muted, a muted pencil;
 * - Check Bison alone: red at 600 with an alert mark, since an unknown send outcome risks a second.
 * The same mark and word name the state on a master-inbox row and at the head of the agent's item,
 * and the Status menu's Reply agent rows use the same words (`list.tsx`).
 */
import type { InboxAgentState } from "../../src/inbox-v2/agent-contract.ts";
import "./agent-chip.css";

/** Whose move the state is: the rep's, the agent's (waiting), or a send to check in Bison. */
type AgentMark = "move" | "wait" | "alert";

export const AGENT_CHIPS: Record<InboxAgentState, { label: string; mark: AgentMark }> = {
  ready_to_call: { label: "Ready to call", mark: "move" },
  draft_ready: { label: "Draft ready", mark: "move" },
  needs_answer: { label: "Needs your answer", mark: "move" },
  drafting: { label: "Drafting", mark: "wait" },
  no_draft: { label: "No draft", mark: "wait" },
  sending: { label: "Sending…", mark: "wait" },
  check_bison: { label: "Check Bison", mark: "alert" },
};

/** The agent's pencil: what it writes, at the word's own size. */
function PencilMark() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M4.5 19.5 5.6 15 15.8 4.8a2 2 0 0 1 2.9 0l.5.5a2 2 0 0 1 0 2.9L9 18.4z" />
      <path d="m14 6.6 3.4 3.4" />
    </svg>
  );
}

/** A send whose outcome is unknown: the one state drawn as an alert. */
function AlertMark() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.8v5" />
      <path d="M12 16.2h.01" />
    </svg>
  );
}

export function AgentChip(props: {
  state: InboxAgentState;
  className?: string;
  /** False where "Reply agent" is already said beside it, as at the head of the agent's item. */
  named?: boolean;
}) {
  const chip = AGENT_CHIPS[props.state];
  return (
    <span className={`lcr-agent-chip ${props.className ?? ""}`} data-mark={chip.mark}>
      {/* Read apart from the label beside it, which is the lead's meaning, not the agent's. */}
      {props.named !== false && <span className="lcr-visually-hidden">Reply agent: </span>}
      {chip.mark === "alert" ? <AlertMark /> : <PencilMark />}
      {chip.label}
    </span>
  );
}
